<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-btn icon class="mr-2" @click="goBack">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="text-h5">{{ keybox.name || 'Keybox Details' }}</span>
            <v-chip
              v-if="keybox.isCurrent"
              color="success"
              text-color="white"
              small
              class="ml-4"
            >
              Current
            </v-chip>
            <v-spacer></v-spacer>
            <v-btn 
              color="primary" 
              class="mr-2"
              @click="editKeybox"
            >
              Edit
            </v-btn>
            <v-btn
              v-if="!keybox.isCurrent"
              color="primary"
              outlined
              class="mr-2"
              @click="confirmSetCurrent"
              title="Set as Current"
            >
              Set as Current
            </v-btn>
            <v-btn
              v-if="!keybox.isCurrent"
              color="error"
              text
              @click="confirmDelete"
            >
              Delete
            </v-btn>
          </v-card-title>
          
          <v-card-text v-if="loading">
            <v-skeleton-loader
              type="article"
              class="mx-auto"
            ></v-skeleton-loader>
          </v-card-text>
          
          <v-card-text v-else>
            <v-row>
              <v-col cols="12" md="6">
                <v-simple-table>
                  <template #default>
                    <tbody>
                      <tr>
                        <td class="font-weight-bold">ID</td>
                        <td>{{ keybox._id }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">Name</td>
                        <td>{{ keybox.name }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">Description</td>
                        <td>{{ keybox.description || 'No description' }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">Created</td>
                        <td>{{ new Date(keybox.createdAt).toLocaleString() }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">Last Updated</td>
                        <td>{{ new Date(keybox.updatedAt).toLocaleString() }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">Status</td>
                        <td>
                          <v-chip
                            :color="keybox.isCurrent ? 'success' : 'gray'"
                            text-color="white"
                            small
                          >
                            {{ keybox.isCurrent ? 'Current' : 'Not Current' }}
                          </v-chip>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
            
            <!-- EC Keys Section -->
            <v-row class="mt-4">
              <v-col cols="12">
                <v-card outlined class="mb-4">
                  <v-card-title class="text-subtitle-1">
                    EC Keys
                  </v-card-title>
                  <v-card-text>
                    <div class="mb-4">
                      <div class="d-flex align-center mb-2">
                        <h3 class="subtitle-1">EC Private Key</h3>
                        <v-spacer></v-spacer>
                        <v-btn
                          small
                          text
                          color="primary"
                          @click="copyToClipboard(keybox.ecPriv)"
                          :disabled="!keybox.ecPriv"
                        >
                          <v-icon small left>mdi-content-copy</v-icon>
                          Copy
                        </v-btn>
                      </div>
                      <v-card outlined class="pa-2" v-if="keybox.ecPriv">
                        <pre class="key-data">{{ keybox.ecPriv }}</pre>
                      </v-card>
                      <v-alert
                        v-else
                        type="info"
                        text
                        dense
                      >
                        No EC private key available
                      </v-alert>
                    </div>
                    
                    <div>
                      <div class="d-flex align-center mb-2">
                        <h3 class="subtitle-1">EC Certificates ({{ (keybox.ecCerts || []).length }})</h3>
                      </div>
                      
                      <div v-if="keybox.ecCerts && keybox.ecCerts.length">
                        <v-expansion-panels>
                          <v-expansion-panel
                            v-for="(cert, index) in keybox.ecCerts"
                            :key="`ec-cert-${index}`"
                          >
                            <v-expansion-panel-header>
                              EC Certificate {{ index + 1 }}
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div class="d-flex align-center">
                                <v-spacer></v-spacer>
                                <v-btn
                                  small
                                  text
                                  color="primary"
                                  @click="copyToClipboard(cert)"
                                >
                                  <v-icon small left>mdi-content-copy</v-icon>
                                  Copy
                                </v-btn>
                              </div>
                              <v-card outlined class="pa-2 mt-1">
                                <pre class="key-data">{{ cert }}</pre>
                              </v-card>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </div>
                      
                      <v-alert
                        v-else
                        type="info"
                        text
                        dense
                      >
                        No EC certificates available
                      </v-alert>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            
            <!-- RSA Keys Section -->
            <v-row>
              <v-col cols="12">
                <v-card outlined>
                  <v-card-title class="text-subtitle-1">
                    RSA Keys
                  </v-card-title>
                  <v-card-text>
                    <div class="mb-4">
                      <div class="d-flex align-center mb-2">
                        <h3 class="subtitle-1">RSA Private Key</h3>
                        <v-spacer></v-spacer>
                        <v-btn
                          small
                          text
                          color="primary"
                          @click="copyToClipboard(keybox.rsaPriv)"
                          :disabled="!keybox.rsaPriv"
                        >
                          <v-icon small left>mdi-content-copy</v-icon>
                          Copy
                        </v-btn>
                      </div>
                      <v-card outlined class="pa-2" v-if="keybox.rsaPriv">
                        <pre class="key-data">{{ keybox.rsaPriv }}</pre>
                      </v-card>
                      <v-alert
                        v-else
                        type="info"
                        text
                        dense
                      >
                        No RSA private key available
                      </v-alert>
                    </div>
                    
                    <div>
                      <div class="d-flex align-center mb-2">
                        <h3 class="subtitle-1">RSA Certificates ({{ (keybox.rsaCerts || []).length }})</h3>
                      </div>
                      
                      <div v-if="keybox.rsaCerts && keybox.rsaCerts.length">
                        <v-expansion-panels>
                          <v-expansion-panel
                            v-for="(cert, index) in keybox.rsaCerts"
                            :key="`rsa-cert-${index}`"
                          >
                            <v-expansion-panel-header>
                              RSA Certificate {{ index + 1 }}
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div class="d-flex align-center">
                                <v-spacer></v-spacer>
                                <v-btn
                                  small
                                  text
                                  color="primary"
                                  @click="copyToClipboard(cert)"
                                >
                                  <v-icon small left>mdi-content-copy</v-icon>
                                  Copy
                                </v-btn>
                              </div>
                              <v-card outlined class="pa-2 mt-1">
                                <pre class="key-data">{{ cert }}</pre>
                              </v-card>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </div>
                      
                      <v-alert
                        v-else
                        type="info"
                        text
                        dense
                      >
                        No RSA certificates available
                      </v-alert>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            
            <!-- Build Properties Section -->
            <v-row class="mt-4">
              <v-col cols="12">
                <v-card outlined>
                  <v-card-title class="text-subtitle-1 d-flex align-center">
                    Build Properties
                    <v-icon color="primary" class="ml-2">mdi-android</v-icon>
                    <v-spacer></v-spacer>
                    <div>
                      <v-btn
                        small
                        text
                        color="primary"
                        class="mr-2"
                        @click="copyToClipboard(keybox.propsToEdit)"
                        :disabled="!keybox.propsToEdit"
                      >
                        <v-icon small left>mdi-content-copy</v-icon>
                        Copy Properties
                      </v-btn>
                      <v-btn
                        small
                        text
                        color="primary"
                        @click="copyToClipboard(keybox.propsValues)"
                        :disabled="!keybox.propsValues"
                      >
                        <v-icon small left>mdi-content-copy</v-icon>
                        Copy Values
                      </v-btn>
                    </div>
                  </v-card-title>
                  
                  <v-card-text>
                    <v-alert
                      v-if="!keybox.propsToEdit || !keybox.propsValues"
                      type="info"
                      text
                      dense
                    >
                      No build properties configured
                    </v-alert>
                    
                    <template v-else>
                      <div class="mb-4">
                        <h3 class="subtitle-1 mb-2">Properties to Edit</h3>
                        <v-card outlined class="pa-2">
                          <pre class="key-data">{{ keybox.propsToEdit }}</pre>
                        </v-card>
                      </div>
                      
                      <div class="mb-4">
                        <h3 class="subtitle-1 mb-2">Property Values</h3>
                        <v-card outlined class="pa-2">
                          <pre class="key-data">{{ keybox.propsValues }}</pre>
                        </v-card>
                      </div>
                      
                      <!-- Property Mapping Table -->
                      <div class="mt-4">
                        <h3 class="subtitle-1 mb-2">Property Mapping</h3>
                        <v-simple-table dense>
                          <template #default>
                            <thead>
                              <tr>
                                <th>Property</th>
                                <th>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(pair, index) in propsMappingPairs" :key="index">
                                <td class="font-weight-bold">{{ pair.key }}</td>
                                <td>{{ pair.value }}</td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>
                    </template>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Confirmation Dialog -->
    <v-dialog
      v-model="confirmDialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title class="text-h5">{{ confirmTitle }}</v-card-title>
        <v-card-text>{{ confirmMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="error" v-if="confirmAction === 'delete'" @click="deleteKeybox">Delete</v-btn>
          <v-btn color="primary" v-if="confirmAction === 'setCurrent'" @click="setAsCurrent">Set as Current</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Snackbar for notifications and copy confirmations -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="4000"
    >
      {{ snackbarText }}
      <template #action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { clientApi } from '@/services/api'

export default {
  name: 'KeyboxDetailRoute',
  
  props: {
    id: {
      type: String,
      required: true
    }
  },
  
  data() {
    return {
      keybox: {},
      loading: true,
      
      // Confirmation dialog
      confirmDialog: false,
      confirmTitle: '',
      confirmMessage: '',
      confirmAction: null,
      
      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success'
    }
  },
  
  computed: {
    // Compute key-value pairs for build properties display
    propsMappingPairs() {
      if (!this.keybox.propsToEdit || !this.keybox.propsValues) {
        return [];
      }
      
      const keys = this.keybox.propsToEdit.split(',').map(k => k.trim());
      const values = this.keybox.propsValues.split(',').map(v => v.trim());
      
      // Create pairs up to the shorter array length
      const pairs = [];
      const minLength = Math.min(keys.length, values.length);
      
      for (let i = 0; i < minLength; i++) {
        pairs.push({
          key: keys[i],
          value: values[i]
        });
      }
      
      return pairs;
    }
  },
  
  created() {
    this.fetchKeybox()
  },
  
  methods: {
    async fetchKeybox() {
      this.loading = true
      
      try {
        const response = await clientApi.getKeyboxById(this.id)
        this.keybox = response.data
      } catch (error) {
        console.error('Error fetching keybox:', error)
        this.showSnackbar('Failed to load keybox details', 'error')
      } finally {
        this.loading = false
      }
    },
    
    goBack() {
      this.$router.push({ name: 'Keyboxes' })
    },
    
    editKeybox() {
      // Use the router to navigate to the Keyboxes route with a query param 
      // to open the edit dialog for this specific keybox
      this.$router.push({ 
        name: 'Keyboxes',
        query: { edit: this.id }
      })
    },
    
    confirmDelete() {
      this.confirmTitle = 'Delete Keybox'
      this.confirmMessage = `Are you sure you want to delete the keybox "${this.keybox.name}"? This action cannot be undone.`
      this.confirmAction = 'delete'
      this.confirmDialog = true
    },
    
    async deleteKeybox() {
      try {
        await clientApi.deleteKeybox(this.id)
        this.confirmDialog = false
        this.showSnackbar('Keybox deleted successfully')
        // Navigate back to the keyboxes list
        this.$router.push({ name: 'Keyboxes' })
      } catch (error) {
        console.error('Error deleting keybox:', error)
        this.showSnackbar(`Failed to delete keybox: ${error.message}`, 'error')
      }
    },
    
    confirmSetCurrent() {
      this.confirmTitle = 'Set as Current Keybox'
      this.confirmMessage = `Are you sure you want to set "${this.keybox.name}" as the current keybox? This will mark all other keyboxes as not current.`
      this.confirmAction = 'setCurrent'
      this.confirmDialog = true
    },
    
    async setAsCurrent() {
      try {
        await clientApi.setKeyboxAsCurrent(this.id)
        this.confirmDialog = false
        this.showSnackbar(`${this.keybox.name} is now the current keybox`)
        // Refresh the keybox data
        this.fetchKeybox()
      } catch (error) {
        console.error('Error setting current keybox:', error)
        this.showSnackbar(`Failed to set current keybox: ${error.message}`, 'error')
      }
    },
    
    copyToClipboard(text) {
      if (!text) return
      
      // Use the clipboard API to copy text
      navigator.clipboard.writeText(text)
        .then(() => {
          this.showSnackbar('Copied to clipboard')
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err)
          this.showSnackbar('Failed to copy to clipboard', 'error')
        })
    },
    
    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.key-data {
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 300px;
  overflow-y: auto;
  font-family: monospace;
  font-size: 0.85rem;
  margin: 0;
}
</style>